import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RotateCw } from 'lucide-react';
import styles from './ErrorState.module.scss';

interface ErrorStateProps {
    /** Already-translated, page-specific sentence. */
    message: string;
    /**
     * Re-run whatever failed. Omit only where there is genuinely nothing to
     * retry — a page that renders this without one is the dead end F30 was
     * about.
     */
    onRetry?: () => void;
    /** Label for the retry control, when "Try again" is not the right verb. */
    retryLabel?: string;
}

/**
 * The shared failure view.
 *
 * F11 made eight pages able to report a failure; each reported it as one
 * sentence with no way forward, so the only recovery was a browser reload
 * (F30). Every error branch now goes through here and offers an action.
 */
const ErrorState: React.FC<ErrorStateProps> = ({ message, onRetry, retryLabel }) => {
    const { t } = useTranslation();

    return (
        <div className={styles.errorState} role="alert">
            <AlertTriangle className={styles.icon} size={40} strokeWidth={1.5} aria-hidden="true" />
            <p className={styles.message}>{message}</p>
            {onRetry && (
                <button type="button" className={styles.retry} onClick={onRetry}>
                    <RotateCw size={18} strokeWidth={2} aria-hidden="true" />
                    {retryLabel || t('try_again')}
                </button>
            )}
        </div>
    );
};

export default ErrorState;
